import React from 'react';
import {
  GuidePage, Section, Steps, QuestionList, WhatHappensNext, Trouble, Footnote, Para, Bullets, Callout,
  startCaseStep, subjectRow, troubleSleeveRows, TROUBLE_CATEGORY_MISSING, TROUBLE_WRONG_HOUSEHOLD,
} from '../GuideKit';

/* Operations → Add / Update Paperwork → Add / Update Paperwork / Missing Paperwork Follow-Up.
 *
 * Written against the live flow Prosperity_Workflow_Create_New_Case_Ops
 * (version active 2026-10-04): screens "Add / Update Paperwork Screen" /
 * "Missing Paperwork Screen", then "Attach Add / Update Paperwork Files
 * Screen" / "Attach Missing Paperwork Follow-Up Files Screen". The two
 * sub-categories are the same form; only the first question's option labels,
 * the paperwork question's wording and the workflow template differ. Shared
 * chrome and policy copy live in GuideKit.js.
 */

const STEPS = [
  startCaseStep({
    recordType: 'Operations',
    category: 'Add / Update Paperwork',
    subCategory: 'Add / Update Paperwork, or Missing Paperwork Follow-Up',
    subject: subjectRow('Updated Schedule A - 0989-022356'),
    lookups: [['Household', 'The client household. It decides which accounts you can pick later, and the advisor and office are taken from it.']],
    note: (
      <>
        <strong>Both sub-categories ask the same questions.</strong> Use <em>Add / Update Paperwork</em> when you are
        sending new or replacement paperwork, and <em>Missing Paperwork Follow-Up</em> when you are supplying paperwork
        that is outstanding. Only the wording of a few labels changes - both are shown in the reference below.
      </>
    ),
  }),
  {
    title: 'Answer the paperwork questions',
    body: (
      <p style={{ margin: 0 }}>
        The screen is headed <strong>Add / Update Paperwork Details</strong> (or <strong>Missing Paperwork
        Details</strong>). Questions reveal themselves one at a time based on your answers, so you only see what
        applies. Every question in the reference below is required once it appears.
      </p>
    ),
  },
  {
    title: 'Write the instructions and choose the accounts',
    body: (
      <>
        <p style={{ marginBottom: '0.5rem' }}>
          Once the paperwork questions are answered, an <strong>Additional Instructions</strong> box appears. It
          becomes the case description Operations reads first, so say what the paperwork is for and anything
          Operations needs to do with it.
        </p>
        <p style={{ margin: 0 }}>
          When it has something in it, the table <strong>Please Select the Applicable Accounts</strong> appears.
          Tick every account the paperwork applies to - you can pick several. It shows Name, Account Details, Model
          and Balance.
        </p>
      </>
    ),
  },
  {
    title: 'Attach the paperwork, acknowledge and submit',
    body: (
      <>
        <p style={{ marginBottom: '0.5rem' }}>
          The next screen, <strong>Attach Add / Update Paperwork Files</strong> (or <strong>Attach Missing Paperwork
          Follow-Up Files</strong>), says: <em>Please add any related Documents to the tiles below. Please note,
          missing or incomplete documents may result in a NIGO.</em> Add the signed paperwork there. The files are
          linked to the case when it is created.
        </p>
        <p style={{ margin: 0 }}>
          Read the Operations and Trading notice below the tiles. Select <em>I have read, understand, and agree to the
          above information</em> under Acknowledgement and click Next. FSC creates the case and opens it. Note the
          case number for follow-up.
        </p>
      </>
    ),
  },
];

// Every question on the Add / Update Paperwork Screen (and its Missing
// Paperwork twin), in the order it can appear.
const QUESTIONS = [
  {
    q: 'Does this apply to the entire Household, Specific Accounts or Both?',
    when: 'Always - this is the first question',
    options: ['Add/Update (Entire Household)', 'Add/Update (Specific Accounts)', 'Add/Update (Other)'],
    note: (
      <>
        On <em>Missing Paperwork Follow-Up</em> the options read <em>Missing Paperwork (Entire Household)</em>,{' '}
        <em>Missing Paperwork (Specific Accounts)</em> and <em>Missing Paperwork (Other)</em>. The help text
        explains: &ldquo;Example: You could be changing the Fee Schedule for the entire Household however, certain
        models may have a higher fee schedule (such as Orion Custom Indexing). Or, you could simply just be changing
        Models, which could result in an LPOA and/or a Fee Schedule change to be signed.&rdquo;
      </>
    ),
  },
  {
    q: 'Add/Update (Other) Details · Missing Paperwork (Other) Details',
    when: 'Only if you chose the (Other) option above',
    note: 'One short line describing what the request covers.',
  },
  {
    q: 'Please select the Prosperity paperwork that you need to update/add (You may select multiple options)',
    when: 'Once the first question is answered',
    options: ['VTA', 'IPS', 'DOL', 'FPA', 'SMA', 'Schedule A', 'Account Agreement', 'FP Planning Agreement', 'FP Ongoing Fee Agreement', 'Annuity Fee Disclosure', 'Alternative Investment Disclosure', 'More / Other / No Prosperity Paperwork'],
    note: (
      <>
        On <em>Missing Paperwork Follow-Up</em> this reads <em>Please select the applicable missing paperwork (You
        may select multiple options)</em>, with the same options. Tick every document involved.
      </>
    ),
  },
  {
    q: 'More / Other Details / No Prosperity Paperwork Details (If no Prosperity Paperwork is being submitted, please type "N/A")',
    when: 'Only if you ticked More / Other / No Prosperity Paperwork',
    note: 'Name the other paperwork, or type N/A if the request is custodial paperwork only.',
  },
  {
    q: 'Does this involve any custodial paperwork?',
    when: 'Once at least one Prosperity paperwork option is ticked',
    options: ['Yes', 'No'],
    note: 'Answering No skips the three custodial questions below and goes straight to Additional Instructions.',
  },
  {
    q: 'Which Custodian?',
    when: 'Only if you answered Yes to custodial paperwork',
    options: ['Schwab', 'Fidelity'],
  },
  {
    q: 'Please select the Custodial Paperwork (You may select multiple options)',
    when: 'Once a custodian is selected',
    options: ['LPOA / Trading Authorization', 'POA', 'W9', 'More / Other'],
  },
  {
    q: 'More / Other Details (How many of each were submitted, were you provided a tracking number? etc...)',
    when: 'Once any custodial paperwork is ticked - not only More / Other',
    note: 'Say how many of each form were submitted and give any tracking number you were given.',
  },
  {
    q: 'Additional Instructions',
    when: 'Once you answer No to custodial paperwork, or once the custodial More / Other Details box is filled in',
    note: 'Becomes the case description. The accounts table appears once this has something in it.',
  },
];

const TROUBLE = [
  TROUBLE_CATEGORY_MISSING('Operations', 'Add / Update Paperwork'),
  troubleSleeveRows('Inactive sleeves, and cancelled sleeves ending in CNX, are filtered out of the table.'),
  {
    h: "The accounts table hasn't appeared",
    p: ['It stays hidden until the Additional Instructions box has something in it.'],
  },
  {
    h: "I can't find the custodial questions",
    p: ['They only appear after you tick at least one option in the Prosperity paperwork question and answer Yes to Does this involve any custodial paperwork? If the request is custodial paperwork only, tick More / Other / No Prosperity Paperwork and type N/A in the details box that appears.'],
  },
  TROUBLE_WRONG_HOUSEHOLD,
];

export default function AddUpdatePaperwork() {
  return (
    <GuidePage
      title="Add / Update Paperwork Requests"
      icon="file-earmark-text"
      path="Operations → Add / Update Paperwork → Add / Update Paperwork or Missing Paperwork Follow-Up"
      minutes={5}
      intro="How to send new, updated or missing paperwork to the Prosperity Operations team - what to gather first, every question you will be asked, and what happens after you submit."
    >
      <Section title="Before you start" icon="clipboard-check">
        <Para>Have these ready. The form branches on your answers, and you attach the paperwork before you submit.</Para>
        <Bullets items={[
          <><strong>The paperwork itself</strong>, signed and scanned - the Prosperity documents (VTA, IPS, DOL, Schedule A and so on) and any custodial forms. You upload them on the last screen.</>,
          <><strong>Whether it covers the whole household or specific accounts</strong>, and which accounts. You pick them from a list, so you do not need account numbers memorised.</>,
          <><strong>For custodial paperwork</strong>, the custodian (Schwab or Fidelity), how many of each form were submitted and any tracking number you were given.</>,
        ]} />
        <Callout tone="warn">
          The upload screen warns that missing or incomplete documents may result in a NIGO. Attach every document you
          ticked in the form.
        </Callout>
      </Section>

      <Section title="Submitting the request" icon="list-ol">
        <Steps steps={STEPS} />
      </Section>

      <Section title="Every question, and when it appears" icon="question-circle">
        <Para>Use this to prepare before you sit down with a client, or to work out why a question you expected has not shown up.</Para>
        <QuestionList questions={QUESTIONS} />
      </Section>

      <WhatHappensNext team="operations" />

      <Trouble items={TROUBLE} />

      <Footnote>
        This guide describes the request form only. It does not describe which paperwork a given account or model
        requires.
      </Footnote>
    </GuidePage>
  );
}
